import React, { useMemo } from "react";

import { useDispatch, useSelector } from "react-redux";

import CheckboxLabel from "@busbud/design-system-components/dist/CheckboxLabel";
import {
  IconMoon,
  IconStars,
  IconSun,
  IconSunrise,
  Label
} from "@busbud/horizon";

import { useAppContext } from "@app/helpers/hooks";

import type { DepartureTimeRange } from "../../state/filters";

import { FilterCollapse } from "./filter-collapse";
import { departure_time_data } from "./helpers";
import { useFacetedSearch } from "./hooks/useFacetedSearch";
import { getSelectedDepartureTimeRanges } from "../../selectors/get-selected-departure-range";
import {
  addDepartureTimeRange,
  removeDepartureTimeRange
} from "../../state/filters";

export const DepartureTimeFilter = () => {
  const dispatch = useDispatch();
  const { translator } = useAppContext();

  const faceted_search = useFacetedSearch();

  const aggregation = faceted_search.getAggregation("departure_time");

  const selected_departure_time_ranges: string[] = useSelector(
    getSelectedDepartureTimeRanges
  );

  function handleCheckboxChange(key: string | number) {
    const stringified_key = `${key}` as DepartureTimeRange;

    return selected_departure_time_ranges.includes(stringified_key)
      ? dispatch(removeDepartureTimeRange(stringified_key))
      : dispatch(addDepartureTimeRange(stringified_key));
  }

  const icon_map = {
    nighttime: (
      <IconStars size="md" className="text-color-scheme-literal-purple-500" />
    ),
    early: (
      <IconSunrise
        size="md"
        className="text-color-scheme-functional-special-500"
      />
    ),
    midday: (
      <IconSun size="md" className="text-color-scheme-functional-notice-500" />
    ),
    late: (
      <IconMoon
        size="md"
        className="text-color-scheme-functional-informative-500"
      />
    )
  };
  const time_ranges = ["nighttime", "early", "midday", "late"] as const;

  const bucketsData = useMemo(() => {
    // construct default filterable departure times object
    const departure_count_per_time_range = aggregation?.buckets.reduce(
      (acc, item) => {
        acc[item.key as keyof typeof acc] = item.doc_count;

        return acc;
      },
      {
        nighttime: 0,
        early: 0,
        midday: 0,
        late: 0
      }
    );

    return time_ranges.map(key => ({
      key,
      // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
      is_disabled: departure_count_per_time_range?.[key] <= 0 || false,
      labels: departure_time_data[key](
        translator,
        departure_count_per_time_range?.[key] || 0
      ),
      Icon: icon_map[key],
      value: selected_departure_time_ranges.includes(`${key}`)
    }));
  }, [aggregation, selected_departure_time_ranges, translator]);

  return (
    <FilterCollapse title={translator.t("!results.filters.departure.time")}>
      {bucketsData?.map(({ key, is_disabled, Icon, labels, value }) => (
        <div className="flex items-center justify-between" key={key}>
          <CheckboxLabel
            shape="square"
            outlined
            startIcon={Icon}
            disabled={is_disabled}
            label={labels?.countLabel}
            value={value}
            checked={value}
            onChange={() => handleCheckboxChange(key)}
          />
          <Label className="text-color-tertiary" as="p">
            {labels?.label}
          </Label>
        </div>
      ))}
    </FilterCollapse>
  );
};
